import { LocalizedLink } from "@/components/LocalizedLink";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Clock } from "lucide-react";
import { productKeywords } from "@/data/keywordContentMap";

interface Article {
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  readTime: string;
}

interface RelatedArticlesProps {
  currentSlug: string;
  className?: string;
}

const articles: Article[] = [
  {
    title: "Smart Toilet Reviews 2025: Expert Analysis & Comparison Guide",
    slug: "/blog/smart-toilet-reviews-2025",
    excerpt: "In-depth smart toilet reviews for 2025 with expert testing and real user feedback.",
    category: "Reviews",
    readTime: "18 min"
  },
  {
    title: "Luxury Smart Toilet Buying Guide 2025",
    slug: "/blog/luxury-smart-toilet-guide",
    excerpt: "Complete guide to choosing the perfect luxury smart toilet for your home.",
    category: "Buying Guide",
    readTime: "15 min"
  },
  {
    title: "Smart Toilet vs Bidet: Which is Right for You?",
    slug: "/blog/smart-toilet-vs-bidet",
    excerpt: "Compare smart toilets and bidets to make the best choice for your bathroom.",
    category: "Comparison",
    readTime: "12 min"
  },
  {
    title: "Smart Toilet Installation Guide 2025",
    slug: "/blog/smart-toilet-installation-guide",
    excerpt: "Step-by-step installation guide with professional tips and requirements.",
    category: "Installation",
    readTime: "14 min"
  },
  {
    title: "Aureum VII Collection Overview",
    slug: "/blog/aureum-vii-collection",
    excerpt: "Explore the complete Aureum VII Trading luxury smart toilet collection.",
    category: "Collection",
    readTime: "10 min"
  },
  {
    title: "Affordable Smart Toilets 2025: Best Value Guide",
    slug: "/blog/affordable-smart-toilets",
    excerpt: "Discover affordable smart toilets that deliver premium features without premium prices.",
    category: "Value Guide",
    readTime: "14 min"
  },
  {
    title: "Health Benefits of Smart Toilets",
    slug: "/blog/health-benefits-smart-toilets",
    excerpt: "Discover the health and wellness benefits of modern smart toilet technology.",
    category: "Health",
    readTime: "11 min"
  },
  {
    title: "Bathroom Remodel Planning Guide 2025",
    slug: "/blog/bathroom-remodel-guide",
    excerpt: "Expert bathroom remodel guide featuring smart toilet integration.",
    category: "Design",
    readTime: "15 min"
  },
  {
    title: "The Health Benefits of Bidet Toilets",
    slug: "/blog/bidet-health-benefits",
    excerpt: "Explore the proven health benefits of bidet toilets for improved hygiene.",
    category: "Health",
    readTime: "13 min"
  },
  {
    title: "Smart Toilets for Seniors: Safety & Comfort",
    slug: "/blog/smart-toilets-for-seniors",
    excerpt: "How smart toilets enhance safety and independence for elderly users.",
    category: "Wellness",
    readTime: "11 min"
  }
];

export function RelatedArticles({ currentSlug, className = "" }: RelatedArticlesProps) {
  // Filter out current article and get 3 related articles
  const relatedArticles = articles
    .filter(a => a.slug !== currentSlug)
    .slice(0, 3);

  // Get keyword-based internal links (for SEO crawlability)
  const relevantKeywords = productKeywords
    .filter(k => k.relevanceScore >= 4)
    .slice(0, 6);

  return (
    <section className={`py-16 bg-gradient-to-br from-muted/30 to-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Continue Reading
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explore more expert guides and insights on luxury smart toilet technology
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {relatedArticles.map((article) => (
            <Card key={article.slug} className="group hover:shadow-xl transition-all duration-500 overflow-hidden border-border/50">
              <CardContent className="p-6">
                <Badge variant="outline" className="mb-3">{article.category}</Badge>
                <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {article.readTime}
                  </span>
                </div>
                <Button asChild variant="outline" className="w-full group/btn">
                  <LocalizedLink to={article.slug}>
                    Read Article
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </LocalizedLink>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* SEO Internal Links - Crawlable keyword links */}
        <div className="mt-12 pt-8 border-t border-border/30">
          <h3 className="text-lg font-semibold text-center mb-6 text-muted-foreground">
            Explore Our Smart Toilet Solutions
          </h3>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {relevantKeywords.map((kw, index) => (
              <LocalizedLink 
                key={`${kw.keyword}-${index}`}
                to={kw.targetUrl}
                className="px-4 py-2 bg-muted/50 hover:bg-primary/10 rounded-full text-sm text-foreground hover:text-primary transition-colors border border-border/30"
                title={kw.anchorText}
              >
                {kw.anchorText}
              </LocalizedLink>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <Button variant="outline" size="lg" asChild>
            <LocalizedLink to="/blog">
              View All Articles
              <ArrowRight className="w-4 h-4 ml-2" />
            </LocalizedLink>
          </Button>
        </div>
      </div>
    </section>
  );
}